/**
 * Attendee QR Code Modal
 * Displays a QR code for event attendee check-in
 */

import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import Button from "../common/Button";
import { XMarkIcon, QrCodeIcon } from "@heroicons/react/24/outline";

interface AttendeeQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  attendeeName: string;
  attendeeEmail?: string;
  checkinCode: string;
  eventTitle?: string;
}

const AttendeeQRCodeModal: React.FC<AttendeeQRCodeModalProps> = ({
  isOpen,
  onClose,
  attendeeName,
  attendeeEmail,
  checkinCode,
  eventTitle,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(true);

  // The supported check-in surface. /checkin was retired 2026-09-23: its lookup RPC referenced
  // columns a schema rename had removed, so every QR this modal ever produced pointed at a page
  // that threw. /self-checkin?code= resolves a CI- admission code by design.
  const checkinUrl = `${window.location.origin}/self-checkin?code=${checkinCode}`;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const generateQRCode = async () => {
      setIsGenerating(true);
      try {
        // Generate QR code on canvas
        if (canvasRef.current) {
          await QRCode.toCanvas(canvasRef.current, checkinUrl, {
            width: 280,
            margin: 2,
            color: {
              dark: "#000000",
              light: "#FFFFFF",
            },
          });
        }

        // Also generate data URL for download
        const dataUrl = await QRCode.toDataURL(checkinUrl, {
          width: 600,
          margin: 2,
        });
        setQrDataUrl(dataUrl);
      } catch (error) {
        console.error("Error generating QR code:", error);
      } finally {
        setIsGenerating(false);
      }
    };

    generateQRCode();
  }, [checkinUrl, isOpen]);

  const handleDownload = () => {
    if (qrDataUrl) {
      const link = document.createElement("a");
      link.href = qrDataUrl;
      // Create safe filename from attendee name
      const safeName = attendeeName.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
      link.download = `checkin-${safeName}-${checkinCode}.png`;
      link.click();
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(checkinCode);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(checkinUrl);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <QrCodeIcon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Check-in QR Code
              </h3>
              <p className="text-sm text-gray-500">{attendeeName}</p>
            </div>
          </div>

          {/* QR Code Display */}
          <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg border-2 border-primary-200 mb-4">
            {isGenerating ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-2" />
                <p className="text-sm text-gray-600">Generating QR code...</p>
              </div>
            ) : (
              <canvas ref={canvasRef} />
            )}
          </div>

          {/* Code Details */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg mb-4">
            {eventTitle && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Event
                </label>
                <p className="text-sm font-medium text-gray-900">
                  {eventTitle}
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Attendee
              </label>
              <p className="text-sm font-medium text-gray-900">
                {attendeeName}
              </p>
              {attendeeEmail && (
                <p className="text-sm text-gray-600">{attendeeEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Check-in Code
              </label>
              <div className="flex items-center gap-2">
                <p className="text-lg font-mono font-bold text-primary-600">
                  {checkinCode}
                </p>
                <button
                  onClick={handleCopyCode}
                  className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                >
                  Copy
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Check-in URL
              </label>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-600 break-all flex-1">
                  {checkinUrl}
                </p>
                <button
                  onClick={handleCopyUrl}
                  className="text-xs text-primary-600 hover:text-primary-700 hover:underline flex-shrink-0"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handleDownload}
              variant="secondary"
              disabled={!qrDataUrl}
              className="flex-1"
            >
              Download QR
            </Button>
            <Button onClick={onClose} variant="primary" className="flex-1">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendeeQRCodeModal;
